import { formatDate } from './dateFormat'
import { isRecord } from './entityResponse'

const TYPE_LABELS = Object.freeze({
  upd: 'УПД',
  payment: 'Платежное поручение',
  factura_avance: 'Счет-фактура на аванс',
  avance: 'Счет-фактура на аванс',
  accept_factura_avance: 'Зачет аванса',
  bill_to_pay: 'Счет на оплату',
  act_sver: 'Акт сверки'
})

const ROUTE_TYPES = Object.freeze({
  document_info_upd: 'upd',
  document_info_payment: 'payment',
  document_info_factura_avance: 'factura_avance',
  document_info_bill_to_pay: 'bill_to_pay',
  document_info_act_sver: 'act_sver'
})

const LIFECYCLE = Object.freeze({
  draft: { key: 'draft', label: 'Черновик', tone: 'warning', icon: 'edit_note' },
  posted: { key: 'posted', label: 'Проведен', tone: 'success', icon: 'verified' },
  read_only: { key: 'read_only', label: 'Только чтение', tone: 'info', icon: 'lock' },
  archived: { key: 'archived', label: 'Удален', tone: 'danger', icon: 'archive' },
  historical: { key: 'historical', label: 'Историческая версия', tone: 'info', icon: 'history' },
  active: { key: 'active', label: 'Активен', tone: 'success', icon: 'check_circle' }
})

const EDIT_ROUTES = Object.freeze({
  upd: 'document_edit_upd',
  payment: 'document_edit_payment',
  factura_avance: 'document_edit_factura_avance',
  avance: 'document_edit_factura_avance',
  bill_to_pay: 'document_edit_bill_to_pay'
})

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function normalizeHistory(changes) {
  return (Array.isArray(changes) ? changes : []).filter(isRecord).map((change, index) => ({
    id: change.id ?? `history-${index}`,
    message: String(change.message || 'Изменение документа'),
    date: String(change.date || ''),
    user: String(change.user || '—'),
    file: isRecord(change.file) ? change.file : null
  }))
}

function normalizeFiles(files) {
  return (Array.isArray(files) ? files : []).filter(isRecord).map((file, index) => ({
    ...file,
    id: file.id ?? `file-${index}`,
    name: String(file.name || file.filename || `Файл ${index + 1}`),
    url: String(file.url || '')
  }))
}

function explicitLifecycle(shortData, fullData) {
  const value = firstValue(fullData.lifecycle_status, shortData.lifecycle_status, fullData.status, shortData.status)
  return typeof value === 'string' && ['draft', 'posted', 'read_only'].includes(value) ? value : ''
}

export function documentTypeFromRoute(routeName) {
  return ROUTE_TYPES[routeName] || ''
}

export function normalizeWorkbenchDocument({ shortData = {}, fullData = {}, routeName = '', historyId = 'last' } = {}) {
  const safeShort = isRecord(shortData) ? shortData : {}
  const safeFull = isRecord(fullData) ? fullData : {}
  const body = isRecord(safeFull.body) ? safeFull.body : {}
  const type = firstValue(safeFull.document_type, safeShort.document_type, documentTypeFromRoute(routeName)) || 'unknown'
  const isHistorical = Boolean(historyId && historyId !== 'last')
  const isActive = firstValue(safeFull.is_active, safeShort.is_active) !== false
  const explicit = explicitLifecycle(safeShort, safeFull)
  const lifecycle = isHistorical
    ? LIFECYCLE.historical
    : (!isActive ? LIFECYCLE.archived : (LIFECYCLE[explicit] || LIFECYCLE.active))
  const number = firstValue(safeFull.in_org_number, safeShort.in_org_number, body.in_org_number, body.number)
  const fallbackTitle = `${TYPE_LABELS[type] || 'Документ'}${number ? ` №${number}` : ''}`
  const rawDate = firstValue(safeFull.date_display, safeShort.date_display, safeFull.date, safeShort.date, body.document_date)
  const counterparty = firstValue(
    safeFull.contr_name,
    safeShort.contr_name,
    isRecord(safeFull.counterparty) ? safeFull.counterparty.name : undefined,
    isRecord(body.counterparty) ? body.counterparty.name : undefined,
    isRecord(body.counterparty_snapshot) ? body.counterparty_snapshot.name : undefined,
    body.cust,
    body.rec
  )

  return {
    id: firstValue(safeFull.id, safeShort.id),
    type,
    typeLabel: TYPE_LABELS[type] || `Документ (${type})`,
    number: number === undefined ? '' : String(number),
    title: String(firstValue(safeFull.name, safeShort.name, fallbackTitle)),
    lifecycle,
    lifecycleExplicit: Boolean(explicit),
    isActive,
    isHistorical,
    historyId: historyId || 'last',
    direction: String(firstValue(safeFull.direction, safeShort.direction, body.direction, '')),
    date: formatDate(rawDate, rawDate ? String(rawDate) : '—'),
    counterparty: counterparty ? String(counterparty) : '—',
    closingDebit: type === 'act_sver' ? (body.total?.my_total_debet ?? null) : null,
    closingCredit: type === 'act_sver' ? (body.total?.my_total_credit ?? null) : null,
    amount: type === 'act_sver' ? null : firstValue(safeFull.summ_total, safeShort.summ_total, safeFull.summ, safeShort.summ, body.summ, isRecord(body.summary) ? body.summary.summtotal : undefined),
    history: normalizeHistory(safeFull.changes || safeShort.changes),
    files: normalizeFiles(safeFull.files || safeShort.files),
    transfers: Array.isArray(safeFull.transfers) ? safeFull.transfers : (Array.isArray(safeShort.transfers) ? safeShort.transfers : []),
    body
  }
}

export function documentEditRoute(document, paymentContext = '') {
  const name = EDIT_ROUTES[document.type]
  if (!name || (document.type === 'bill_to_pay' && document.body?.source !== 'accounting')) return null
  return {
    name,
    params: { id: document.id },
    ...(document.type === 'payment' ? { query: { context: paymentContext || 'bank' } } : {})
  }
}

export function documentActionAvailability(document, { canEdit = false, offline = false } = {}) {
  if (!canEdit) return { allowed: false, reason: 'Недостаточно прав для изменения документов.' }
  if (offline) return { allowed: false, reason: 'Действие недоступно без подключения к серверу.' }
  if (document.isHistorical) return { allowed: false, reason: 'Историческую версию нельзя изменять.' }
  if (document.type === 'bill_to_pay' && document.body?.source !== 'accounting') return { allowed: false, reason: 'Счет сформирован биллинговым контуром и доступен только для чтения.' }
  if (document.lifecycle.key === 'posted') return { allowed: false, reason: 'Проведенный документ доступен только для просмотра.' }
  if (document.lifecycle.key === 'read_only') return { allowed: false, reason: 'Сервер пометил документ как доступный только для чтения.' }
  return { allowed: true, reason: '' }
}

export function directionDisplay(direction) {
  return { in: 'Входящий', out: 'Исходящий', internal: 'Внутренний' }[direction] || 'Не указано'
}
